import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { districtService } from '../services/districtService';

export const useAddressData = () => {
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedSubdivision, setSelectedSubdivision] = useState('');
  const [selectedBlock, setSelectedBlock] = useState('');
  const [selectedGPU, setSelectedGPU] = useState('');
  const [selectedWard, setSelectedWard] = useState('');

  // Fetch districts
  const { data: districts = [], isLoading: loadingDistricts, error: errorDistricts } = useQuery({
    queryKey: ['districts'],
    queryFn: districtService.getDistricts,
  });

  // Fetch subdivisions
  const { data: subdivisions = [], isLoading: loadingSubdivisions, error: errorSubdivisions } = useQuery({
    queryKey: ['subdivisions', selectedDistrict],
    queryFn: () => selectedDistrict ? districtService.getSubdivisions('id', selectedDistrict) : Promise.resolve([]),
    enabled: !!selectedDistrict,
  });

  // Fetch blocks
  const { data: blocks = [], isLoading: loadingBlocks, error: errorBlocks } = useQuery({
    queryKey: ['blocks', selectedSubdivision],
    queryFn: () => selectedSubdivision ? districtService.getBlocks(selectedSubdivision) : Promise.resolve([]),
    enabled: !!selectedSubdivision,
  });

  // Fetch GPUs
  const { data: gpus = [], isLoading: loadingGPUs, error: errorGPUs } = useQuery({
    queryKey: ['gpus', selectedBlock],
    queryFn: () => selectedBlock ? districtService.getGPUs(selectedBlock) : Promise.resolve([]),
    enabled: !!selectedBlock,
  });

  // Fetch wards
  const { data: wards = [], isLoading: loadingWards, error: errorWards } = useQuery({
    queryKey: ['wards', selectedGPU],
    queryFn: () => selectedGPU ? districtService.getWards(selectedGPU) : Promise.resolve([]),
    enabled: !!selectedGPU,
  });

  const handleDistrictChange = (districtId) => {
    setSelectedDistrict(districtId);
    setSelectedSubdivision('');
    setSelectedBlock('');
    setSelectedGPU('');
    setSelectedWard('');
  };

  const handleSubdivisionChange = (subdivisionId) => {
    setSelectedSubdivision(subdivisionId);
    setSelectedBlock('');
    setSelectedGPU('');
    setSelectedWard('');
  };

  const handleBlockChange = (blockId) => {
    setSelectedBlock(blockId);
    setSelectedGPU('');
    setSelectedWard('');
  };

  const handleGPUChange = (gpuId) => {
    setSelectedGPU(gpuId);
    setSelectedWard('');
  };

  const handleWardChange = (wardId) => {
    setSelectedWard(wardId);
  };

  const resetSelections = () => {
    setSelectedDistrict('');
    setSelectedSubdivision('');
    setSelectedBlock('');
    setSelectedGPU('');
    setSelectedWard('');
  };

  const setSelections = ({ district, subdivision, block, gpu, ward }) => {
    if (district !== undefined) setSelectedDistrict(district);
    if (subdivision !== undefined) setSelectedSubdivision(subdivision);
    if (block !== undefined) setSelectedBlock(block);
    if (gpu !== undefined) setSelectedGPU(gpu);
    if (ward !== undefined) setSelectedWard(ward);
  };

  // Helper functions to get selected items
  const getSelectedDistrict = () => districts.find(d => d.id === parseInt(selectedDistrict));
  const getSelectedSubdivision = () => subdivisions.find(s => s.id === parseInt(selectedSubdivision));
  const getSelectedBlock = () => blocks.find(b => b.id === parseInt(selectedBlock));
  const getSelectedGPU = () => gpus.find(g => g.id === parseInt(selectedGPU));
  const getSelectedWard = () => wards.find(w => w.id === parseInt(selectedWard));

  // Helper function to get complete address string
  const getCompleteAddress = () => {
    const parts = [];
    const ward = getSelectedWard();
    const gpu = getSelectedGPU();
    const block = getSelectedBlock();
    const subdivision = getSelectedSubdivision();
    const district = getSelectedDistrict();

    if (ward) parts.push(ward.name);
    if (gpu) parts.push(gpu.name);
    if (block) parts.push(block.name);
    if (subdivision) parts.push(subdivision.name);
    if (district) parts.push(`${district.name} (${district.code})`);

    return parts.join(', ');
  };

  const isLoading = loadingDistricts || loadingSubdivisions || loadingBlocks || loadingGPUs || loadingWards;
  const hasError = errorDistricts || errorSubdivisions || errorBlocks || errorGPUs || errorWards;

  return {
    // Data
    districts,
    subdivisions,
    blocks,
    gpus,
    wards,
    
    // Selected values
    selectedDistrict,
    selectedSubdivision,
    selectedBlock,
    selectedGPU,
    selectedWard,
    
    // Change handlers
    handleDistrictChange,
    handleSubdivisionChange,
    handleBlockChange,
    handleGPUChange,
    handleWardChange,
    
    // Utility functions
    resetSelections,
    setSelections,
    getSelectedDistrict,
    getSelectedSubdivision,
    getSelectedBlock,
    getSelectedGPU,
    getSelectedWard,
    getCompleteAddress,
    
    // Loading states
    isLoading,
    loadingDistricts,
    loadingSubdivisions,
    loadingBlocks,
    loadingGPUs,
    loadingWards,
    
    // Error states
    hasError,
    errorDistricts,
    errorSubdivisions,
    errorBlocks,
    errorGPUs,
    errorWards,
  };
};

export default useAddressData;
